import React from 'react';
import type {ReactFlowInstance} from 'reactflow';

import {getZoomAnimationDurationMs} from 'ui-library/reactflow/ReactFlowConstants';
import {FLOWCHART_SIDE_PANEL_WIDTH} from 'ui-library/reactflow/sidepanels/FlowchartSidebar';

export interface CenterNodeParams {
  reactFlowInstance: ReactFlowInstance;
  reactFlowWrapper: React.RefObject<HTMLDivElement>;
  leftSidepanelRef: React.RefObject<HTMLDivElement>;
  rightSidepanelRef: React.RefObject<HTMLDivElement>;
  nodePosition: {x: number; y: number};
  nodeWidth?: number;
  nodeHeight?: number;
}

interface NodeCenter {
  x: number;
  y: number;
}

const RIGHT_SIDEPANEL_FULL_WIDTH_PERCENTAGE = 0.9;

function calculateNodeCenter(
  nodePosition: {x: number; y: number},
  nodeWidth?: number,
  nodeHeight?: number
): NodeCenter {
  return {
    x: nodeWidth != null ? nodePosition.x + nodeWidth / 2 : nodePosition.x,
    y: nodeHeight != null ? nodePosition.y + nodeHeight / 2 : nodePosition.y,
  };
}

function getPanelWidth(panelRef: React.RefObject<HTMLDivElement>): number {
  const panelElement = panelRef.current;
  return panelElement ? panelElement.getBoundingClientRect().width : 0;
}

interface CenterNodeAtScreenPositionParams {
  reactFlowInstance: ReactFlowInstance;
  targetCenterXScreen: number;
  targetCenterYScreen: number;
  wrapperRect: DOMRect;
  nodeCenter: NodeCenter;
}

function centerNodeAtScreenPosition({
  reactFlowInstance,
  targetCenterXScreen,
  targetCenterYScreen,
  wrapperRect,
  nodeCenter,
}: CenterNodeAtScreenPositionParams): void {
  const currentViewport = reactFlowInstance.getViewport();

  // ReactFlow canvas has a left margin of FLOWCHART_SIDE_PANEL_WIDTH
  const targetXRelativeToWrapper = targetCenterXScreen - wrapperRect.left - FLOWCHART_SIDE_PANEL_WIDTH;
  const targetYRelativeToWrapper = targetCenterYScreen - wrapperRect.top;

  const nodeScreenX = nodeCenter.x * currentViewport.zoom + currentViewport.x;
  const nodeScreenY = nodeCenter.y * currentViewport.zoom + currentViewport.y;

  const offsetX = targetXRelativeToWrapper - nodeScreenX;
  const offsetY = targetYRelativeToWrapper - nodeScreenY;

  const newViewportX = currentViewport.x + offsetX;
  const newViewportY = currentViewport.y + offsetY;

  if (!Number.isFinite(newViewportX) || !Number.isFinite(newViewportY)) {
    return;
  }

  reactFlowInstance.setViewport(
    {x: newViewportX, y: newViewportY, zoom: currentViewport.zoom},
    {duration: getZoomAnimationDurationMs()}
  );
}

/**
 * Centers a node in the visible canvas area, accounting for open sidepanels.
 */
export function centerNodeInVisibleCanvas({
  reactFlowInstance,
  reactFlowWrapper,
  leftSidepanelRef,
  rightSidepanelRef,
  nodePosition,
  nodeWidth,
  nodeHeight,
}: CenterNodeParams): void {
  const wrapperElement = reactFlowWrapper.current;
  if (wrapperElement == null) {
    return;
  }

  const wrapperRect = wrapperElement.getBoundingClientRect();
  if (wrapperRect.width <= 0 || wrapperRect.height <= 0) {
    return;
  }

  const nodeCenter = calculateNodeCenter(nodePosition, nodeWidth, nodeHeight);

  const leftPanelWidth = getPanelWidth(leftSidepanelRef);
  const rightPanelWidth = getPanelWidth(rightSidepanelRef);

  const effectiveLeftPanelWidth = leftPanelWidth > 0 ? leftPanelWidth : FLOWCHART_SIDE_PANEL_WIDTH;
  const visibleAreaLeft = wrapperRect.left + effectiveLeftPanelWidth;
  const visibleAreaRight = wrapperRect.right - rightPanelWidth;
  const visibleCanvasWidth = visibleAreaRight - visibleAreaLeft;
  const availableCanvasWidth = wrapperRect.width - effectiveLeftPanelWidth;

  const isRightPanelFullWidth =
    rightPanelWidth > 0 && rightPanelWidth >= availableCanvasWidth * RIGHT_SIDEPANEL_FULL_WIDTH_PERCENTAGE;
  const shouldCenterInTotal = isRightPanelFullWidth || visibleCanvasWidth <= 0;

  const targetCenterYScreen = wrapperRect.top + wrapperRect.height / 2;

  const totalCanvasLeft = wrapperRect.left + effectiveLeftPanelWidth;
  const totalCanvasWidth = wrapperRect.width - effectiveLeftPanelWidth;
  const centerInTotalCanvas = totalCanvasLeft + totalCanvasWidth / 2;
  const centerInVisibleCanvas = visibleAreaLeft + visibleCanvasWidth / 2;

  const targetCenterXScreen = shouldCenterInTotal ? centerInTotalCanvas : centerInVisibleCanvas;

  if (!Number.isFinite(targetCenterXScreen) || !Number.isFinite(targetCenterYScreen)) {
    return;
  }

  centerNodeAtScreenPosition({
    reactFlowInstance,
    targetCenterXScreen,
    targetCenterYScreen,
    wrapperRect,
    nodeCenter,
  });
}
